import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismIcon, PrismToggle } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDropdownsDropdownYield, {
  type Dropdown,
} from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';

import styles from './chart-options-pace.module.css';

export interface PaceLeadtimeOption {
  id: number;
  label: string;
}

interface TriggerSignature {
  Args: {
    isOpen: boolean;
    label: string;
    toggleDropdown: () => void;
  };
  Element: HTMLButtonElement;
}

const Trigger: TOC<TriggerSignature> = <template>
  <GeneralDropdownsTriggersTriggerDropdownIconButton
    @buttonType="secondary"
    @dataTestId="pace-chart-options-trigger"
    @icon={{if @isOpen "chevron-up" "chevron-down"}}
    @iconBefore="view-chart"
    @label={{@label}}
    @small={{true}}
    @toggleDropdown={{@toggleDropdown}}
  />
</template>;

interface HotelAndMarketRevenueInsightDropdownsChartOptionsPaceSignature {
  Args: {
    // Grey out / disable the leadtime options (a pace/pickup filter owns the window).
    disabledOptions: boolean;
    onSelect: (id: number) => void;
    onToggleShowPickupRealised: (value: boolean) => void;
    options: PaceLeadtimeOption[];
    selected: number;
    // Whether the "Pick-up realised" projection is currently shown.
    showPickupRealised: boolean;
    // Whether to render the "Pick-up realised" toggle row at all.
    showPickupToggle: boolean;
  };
  Element: HTMLDivElement;
}

export default class HotelAndMarketRevenueInsightDropdownsChartOptionsPace extends Component<HotelAndMarketRevenueInsightDropdownsChartOptionsPaceSignature> {
  @action
  select(id: number, dropdown: Dropdown): void {
    if (this.args.disabledOptions) {
      return;
    }
    dropdown.toggleDropdown();
    this.args.onSelect(id);
  }

  @action
  toggleShowPickupRealised(dropdown: Dropdown): void {
    dropdown.toggleDropdown();
    this.args.onToggleShowPickupRealised(!this.args.showPickupRealised);
  }

  <template>
    <div class="relative" ...attributes>
      <GeneralDropdownsDropdownYield
        @dropdownClass=".dropdown-target"
        {{! @glint-expect-error: dropdown-yield's signature is commented out, so its args are untyped }}
        @dropdownClassName="dropdown-target top-[44px] right-0 w-[228px]"
        @label={{t "revenue_insight.pace.chart_options"}}
        @selected={{null}}
        @triggerComponent={{Trigger}}
        as |dropdown|
      >
        <div class="flex flex-col py-100">
          {{#each @options as |option|}}
            <button
              class="button--unstyled flex items-center justify-between gap-400 px-300
                {{styles.item}}"
              data-test-id={{concat "pace-chart-options-option-" option.id}}
              disabled={{@disabledOptions}}
              type="button"
              {{on "click" (fn this.select option.id dropdown)}}
            >
              <span
                class="text-300--regular py-200
                  {{if
                    @disabledOptions
                    'text-disabled'
                    'text-neutral-emphasis'
                  }}"
              >
                {{option.label}}
              </span>
              {{#if (eq option.id @selected)}}
                <PrismIcon @name="checkmark" @size="small" />
              {{/if}}
            </button>
          {{/each}}

          {{#if @showPickupToggle}}
            <div
              class="border-neutral-default flex items-center justify-between gap-400 border-t px-300 py-200"
              data-test-id="pace-chart-options-pickup-realised"
            >
              <span
                class="text-300--regular text-neutral-emphasis whitespace-nowrap"
              >
                {{t "revenue_insight.pace.pickup_realised"}}
              </span>
              <PrismToggle
                @isChecked={{@showPickupRealised}}
                @isSmall={{true}}
                @onChange={{fn this.toggleShowPickupRealised dropdown}}
              />
            </div>
          {{/if}}
        </div>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
